import * as _ from 'lodash';
import { useMemo } from 'react';

import {
  AccountTypeGQLType,
  BorrowerAccountTypeGQLType,
  CapitalProviderAccountTypeGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { getFormValues } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/parties/dataMappings/getFormValues';
import { BaseFormFields } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/parties/types';
import { useCreditAgreementStore } from '~/core/routes/settings/implementations/common/CreditAgreementProvider/CreditAgreementProvider';
import { computeDiff } from '~/core/routes/settings/implementations/common/DiffInputs/hooks';
import { Organization } from '~/core/routes/settings/implementations/common/types';

const accountTypeKeyToNameConfig: {
  [key in AccountTypeGQLType]: string;
} = {
  collections: 'Collections Account',
  otherReserveAccount: 'Other Reserve Account',
  parentOperatingAccount: 'Parent Operating Account',
  preFundingAccount: 'Pre Funding Account',
  capitalProviderFundingAccount: 'Capital Provider Funding Account',
  permittedInvestmentsAccount: 'Permitted Investments Account',
  accountIncludedInBorrowingBase: 'Account Included In Borrowing Base',
};

const borrowerAccountTypes: BorrowerAccountTypeGQLType[] = [
  'collections',
  'otherReserveAccount',
  'parentOperatingAccount',
  'preFundingAccount',
  'permittedInvestmentsAccount',
];

const capitalProviderAccountTypes: CapitalProviderAccountTypeGQLType[] = [
  'capitalProviderFundingAccount',
];

/** Existing bank accounts for the party. */
export const usePartyBankAccounts = (
  party: Organization,
  partyType: 'borrowers' | 'capitalProviders',
) => {
  const parties = useCreditAgreementStore(
    (store) => store.creditAgreement?.facilityMechanics.parties,
  );

  return useMemo(() => {
    const borrowers = parties?.borrowers;
    const capitalProviders = parties?.capitalProviders;

    const existingEntity =
      partyType === 'borrowers'
        ? borrowers?.find((entity) => entity.entity.id === party.id)
        : capitalProviders?.find((entity) => entity.entity.id === party.id);

    // During the transitional period when adding a party, `existingEntity`
    // will not yet be present. It'll appear in the CA shortly after.
    return existingEntity?.bankAccounts ?? [];
  }, [party.id, partyType, parties]);
};

/** Bank account types that can be added for this party. */
export const useBankAccountTypeOptions = (
  party: Organization,
  partyType: 'borrowers' | 'capitalProviders',
) => {
  const existingBankAccounts = usePartyBankAccounts(party, partyType);

  return useMemo(() => {
    const allTypes =
      partyType === 'borrowers'
        ? borrowerAccountTypes
        : capitalProviderAccountTypes;

    const allowedTypes = allTypes.filter(
      (accountType) =>
        !existingBankAccounts.some((ba) => ba.type.value === accountType),
    );

    return allowedTypes.map((type) => ({
      value: type,
      label: accountTypeKeyToNameConfig[type],
    }));
  }, [existingBankAccounts, partyType]);
};

export const usePartyDiffResult = (
  partyType: keyof BaseFormFields,
  id: string,
) => {
  return useCreditAgreementStore((store) => {
    const creditAgreement = store.creditAgreement;
    const previousCreditAgreement = store.previousCreditAgreement;

    if (creditAgreement && previousCreditAgreement) {
      const currentValues = getFormValues(creditAgreement);
      const previousValues = getFormValues(previousCreditAgreement);

      const currentParty = currentValues[partyType].find(
        (party) => party.id === id,
      );
      const previousParty = previousValues[partyType].find(
        (party) => party.id === id,
      );

      const diffResult = computeDiff(
        currentParty,
        previousParty,
        _.isEqual,
        String,
      );

      return { diffResult, currentParty, previousParty };
    }
    return undefined;
  }, _.isEqual);
};

export const useBankAccountDiffResult = (
  partyType: 'borrowers' | 'capitalProviders',
  partyId: string,
  accountId: string,
) => {
  return useCreditAgreementStore((store) => {
    const creditAgreement = store.creditAgreement;
    const previousCreditAgreement = store.previousCreditAgreement;

    if (creditAgreement && previousCreditAgreement) {
      const currentValues = getFormValues(creditAgreement);
      const previousValues = getFormValues(previousCreditAgreement);

      const currentParty = currentValues[partyType].find(
        (party) => party.id === partyId,
      );
      const previousParty = previousValues[partyType].find(
        (party) => party.id === partyId,
      );

      const currentBankAccount = currentParty?.bankAccounts.find(
        (ba) => ba.id === accountId,
      );
      const previousBankAccount = previousParty?.bankAccounts.find(
        (ba) => ba.id === accountId,
      );

      const diffResult = computeDiff(
        currentBankAccount,
        previousBankAccount,
        _.isEqual,
        String,
      );

      return { diffResult, currentBankAccount, previousBankAccount };
    }
    return undefined;
  }, _.isEqual);
};

export const usePartiesDiffValues = () => {
  return useCreditAgreementStore((store) => {
    const creditAgreement = store.creditAgreement;
    const previousCreditAgreement = store.previousCreditAgreement;

    if (creditAgreement && previousCreditAgreement) {
      const currentValues = getFormValues(creditAgreement);
      const previousValues = getFormValues(previousCreditAgreement);

      return {
        borrowers: _.unionBy(
          currentValues.borrowers,
          previousValues.borrowers,
          (b) => b.id,
        ).map((party) => {
          // merging in bankaccounts as well
          const previousParty = previousValues.borrowers.find(
            (b) => b.id === party.id,
          );

          if (!previousParty) return party;
          return {
            ...party,
            bankAccounts: _.unionBy(
              party.bankAccounts,
              previousParty.bankAccounts,
              (ba) => ba.id,
            ),
          };
        }),
        capitalProviders: _.unionBy(
          currentValues.capitalProviders,
          previousValues.capitalProviders,
          (cp) => cp.id,
        ).map((party) => {
          // need to merge in bankaccounts as well
          const previousParty = previousValues.capitalProviders.find(
            (b) => b.id === party.id,
          );

          if (!previousParty) return party;
          return {
            ...party,
            bankAccounts: _.unionBy(
              party.bankAccounts,
              previousParty.bankAccounts,
              (ba) => ba.id,
            ),
          };
        }),
        verificationAgents: _.unionBy(
          currentValues.verificationAgents,
          previousValues.verificationAgents,
          (va) => va.id,
        ),
        servicers: _.unionBy(
          currentValues.servicers,
          previousValues.servicers,
          (s) => s.id,
        ),
      };
    }

    return undefined;
  }, _.isEqual);
};
